import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  updateEntityListDataMultiple,
  updateEntityListDataMultipleFromUpdatedEntityData,
  deleteEntityListData,
  deleteEntityListDataMultiple,
} from '../../modules/mockDataCommon'
import {
  findVersionConflict,
  versionConflictBody,
} from '../../modules/versionConflict'
import { requireAuthenticatedUser } from '../../decorators'
import { withSupplierProductAndItemGuard } from '../../modules/supplierProductAndItemConflict'
import { createMockOperation } from '../operations'
import {
  getOrderMSServiceListData,
  getCompanyDataByOrderMSServiceId,
  propagateOrderMSServiceOrServicesAdditions,
  propagateOrderMSServicesChanges,
  propagateOrderMSServiceChanges,
  propagateOrderMSServiceDeletions,
} from './mockData'
import type { ICompany } from '~/types/company/models'

// async function getOrderMSServices(
//   req: RestRequest,
//   res: ResponseFunction,
//   ctx: RestContext
// ) {
//   console.log(`'Returning ordermasterservices.`)
//   return res(ctx.status(200), ctx.json({ items: getOrderMSServiceListData() }))
// }

interface GetCompanyFromOrderMSServiceResponse {
  data: ICompany
}

async function getCompanyFromOrderMSService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyFromOrderMSServiceResponse = {
      data: await getCompanyDataByOrderMSServiceId(id), // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Company from orderMSService ${id}: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

// post

const postOrderMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(
    req,
    res,
    ctx,
    getOrderMSServiceListData,
    undefined,
    propagateOrderMSServiceOrServicesAdditions
  )
}

const patchOrderMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getOrderMSServiceListData, [
    propagateOrderMSServiceChanges,
  ])
}

const patchOrderMSServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const idempotencyKey = req.headers.get('Idempotency-Key')
  if (idempotencyKey) {
    const updatedEntityDataMultiple = await req.json()
    // ⚠ THE VERSION CHECK HAS TO BE HERE TOO, and its absence is why every
    // multi-row msService PATCH in the mock accepted any version at all: the
    // check lives in `updateEntityListDataMultiple`, this branch calls the
    // underlying helper directly, and `update*MSService` always sets
    // `doAsync: true` for an array -- so this is the ONLY branch these requests
    // ever take. The 409 class the mock was fixed to reproduce was unreachable
    // on the one path the app actually uses.
    const conflict = findVersionConflict({
      listData: await getOrderMSServiceListData(),
      submittedRows: updatedEntityDataMultiple,
    })
    if (conflict) {
      return res(ctx.status(409), ctx.json(versionConflictBody(conflict)))
    }
    const data = await updateEntityListDataMultipleFromUpdatedEntityData({
      req,
      updatedEntityDataMultiple,
      getEntityListDataFn: getOrderMSServiceListData,
      postProcessOnceForAllFn: propagateOrderMSServicesChanges,
    })
    const operationId = createMockOperation('PATCH /api/ordermsservices', data)
    return res(
      ctx.status(202),
      ctx.json({
        data: {
          operation_id: operationId,
          status: 'pending',
          operation_type: 'PATCH /api/ordermsservices',
        },
      })
    )
  }
  return updateEntityListDataMultiple(
    req,
    res,
    ctx,
    getOrderMSServiceListData,
    undefined,
    propagateOrderMSServicesChanges
  )
}

const deleteOrderMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(
    req,
    res,
    ctx,
    getOrderMSServiceListData,
    undefined,
    propagateOrderMSServiceDeletions
  )
}

const deleteOrderMSServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListDataMultiple(
    req,
    res,
    ctx,
    getOrderMSServiceListData,
    undefined,
    propagateOrderMSServiceDeletions // 'Service' is correctly singular here
  )
}

export const handlers = [
  {
    method: 'get',
    path: '/api/ordermsservices/:id/company',
    handler: getCompanyFromOrderMSService,
  },
  {
    method: 'post',
    path: '/api/ordermsservices',
    handler: withSupplierProductAndItemGuard(postOrderMSService),
  },
  {
    method: 'patch',
    path: '/api/ordermsservices/:id',
    handler: withSupplierProductAndItemGuard(
      patchOrderMSService,
      getOrderMSServiceListData
    ),
  },
  {
    method: 'patch',
    path: '/api/ordermsservices',
    handler: withSupplierProductAndItemGuard(
      patchOrderMSServices,
      getOrderMSServiceListData
    ),
  },
  {
    method: 'delete',
    path: '/api/ordermsservices',
    handler: deleteOrderMSServices,
  },
  {
    method: 'delete',
    path: '/api/ordermsservices/:id',
    handler: deleteOrderMSService,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
